import { useUser } from '@src/hooks/useUser';
import { useMarketsContext } from '@src/providers';
import { sortMarketCredentialsByStatus, type SortMode } from '@src/utils/status';
import type { Direction } from '@talos/kyoko';
import {
  Box,
  ConnectionStatusEnum,
  ConnectionStatusIndicator,
  FormControlSizes,
  IconName,
  LoaderSizes,
  LoaderTalos,
  MarketLogo,
  NavButton,
  NavIconButton,
  Tbody,
  Td,
  Tooltip,
  Tr,
  isConnectionOnline,
  isConnectionRecovering,
  isTalosUser,
  useObservableValue,
  type MarketCredentialWithConnectionStatus,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import { AdminPaths, TalosAdminPaths } from '../NavigationSidebar/hooks/getNavPath';
import { ConnectionStatusTooltip } from './ConnectionStatusTooltip';
import { MarketDisplayName, StatusTableCellLinks, Wrapper } from './styles';

export const SORT_MODE_LABELS: Record<SortMode, string> = {
  Status: 'Status',
  Market: 'Market',
  Alphabetical: 'A-Z',
};

const ROW_HEIGHT = 32;
export function StatusTable({
  marketCredentialsWithStatus,
  sortMode,
  sortDirection,
}: {
  marketCredentialsWithStatus: MarketCredentialWithConnectionStatus[];
  sortMode: SortMode;
  sortDirection: Direction;
}) {
  const user = useUser();

  const { marketsWithConnTypesSupported } = useMarketsContext();

  const markets = useObservableValue(
    () => marketsWithConnTypesSupported().pipe(map(markets => new Map(markets.map(m => [m.Name, m])))),
    [marketsWithConnTypesSupported]
  );

  const sortedMarketCredentialsWithStatus = useMemo(
    () =>
      sortMarketCredentialsByStatus(marketCredentialsWithStatus, { sortMode, sortDirection, marketsByName: markets }),
    [marketCredentialsWithStatus, sortMode, sortDirection, markets]
  );

  if (!markets || !marketCredentialsWithStatus?.length) {
    return (
      <Box h={marketCredentialsWithStatus.length ? marketCredentialsWithStatus.length * ROW_HEIGHT : '200px'}>
        <LoaderTalos size={LoaderSizes.SMALL} wrapperStyle={{ width: '200px' }} />
      </Box>
    );
  }

  return (
    <Wrapper minWidth="240px" data-testid="status-table">
      <Tbody>
        {sortedMarketCredentialsWithStatus.map(cred => {
          const market = markets.get(cred.credential.Market);
          const connectionStatus = cred.connectionStatus;
          const isRecovering = isConnectionRecovering(connectionStatus);
          const tooltip = isConnectionOnline(connectionStatus) ? null : (
            <ConnectionStatusTooltip connectionStatus={connectionStatus} />
          );
          const filter = encodeURIComponent(cred.credential.Label);

          return (
            <Tr
              key={`${cred.credential.Name}-${cred.credential.CredentialID}-${
                cred.connectionStatus?.ConnectionID ?? 0
              }`}
              data-testid="status-table-row"
            >
              <Td>
                <MarketLogo market={market?.Name} title={market?.DisplayName} size="14px" />
              </Td>
              <MarketDisplayName>{cred.credential.Label}</MarketDisplayName>
              <Td position="relative">
                <StatusTableCellLinks>
                  {isTalosUser(user) && (
                    <Tooltip tooltip="Market Connections">
                      <NavIconButton
                        to={`${TalosAdminPaths.MarketConnections()}?filter=${filter}`}
                        target="_blank"
                        icon={IconName.StatusOnline}
                        size={FormControlSizes.Tiny}
                      />
                    </Tooltip>
                  )}
                  <NavButton
                    to={`${AdminPaths.Credentials()}?filter=${filter}`}
                    target="_blank"
                    endIcon={IconName.ExternalLink}
                    size={FormControlSizes.Tiny}
                  >
                    Credentials
                  </NavButton>
                </StatusTableCellLinks>
                <ConnectionStatusIndicator
                  status={cred.connectionStatus?.Status ?? ConnectionStatusEnum.Unavailable}
                  text=""
                  iconRight
                  popoverText={tooltip}
                  isLoading={isRecovering}
                  size={14}
                />
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Wrapper>
  );
}
